import { GetReq, Headers, PostReq, ResponseMessages } from "src/utils/types";
import axios from "axios";
import { store } from "src/redux/store";
import {
  API_RESULT,
  ErrorResponseCode,
  ROUTES,
  URL_SOURCE_TYPE,
} from "src/utils/enums";
import {
  __DEV__,
  sentPostMessage,
  showPopupWithMessage,
} from "src/utils/utils";
import { setSessionId } from "src/redux/slices/appSlice/AppSlice";
import { v4 as uuidv4 } from "uuid";

export const BASE_URL = process.env.REACT_APP_BASE_URL;

const getHeaders = (): Headers => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: "Bearer " + store?.getState().app.sessionId,
});

export const getAsync = async ({ fullUrl, url }: GetReq) => {
  url = fullUrl || `${BASE_URL}${url}`;

  try {
    const res = await axios.get(url, { headers: getHeaders() });

    return res.data;
  } catch (e: any) {
    updateParentByPostMessage(
      e?.message || "500 or 504",
      ErrorResponseCode.GENERAL_ERROR.toString(),
      API_RESULT.FAILED
    );
    return e.status || e;
  }
};

export const postAsync = async ({ fullUrl, url, body }: PostReq) => {
  const originalUrl = url; // Keep original URL for checking

  url = fullUrl || `${BASE_URL}${url}`;
  try {
    const res = await axios.post(url, body, { headers: getHeaders() });
    return res.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        if (status === 400) {
          // Don't send postMessage for get-iframe-result - let CardInfo.tsx handle it
          const isGetIframeResult =
            originalUrl?.includes("get-iframe-result") ||
            url?.includes("get-iframe-result");
          if (!isGetIframeResult) {
            updateParentByPostMessage(
              errorData?.responseMessage || "400",
              ErrorResponseCode.GENERAL_ERROR.toString(),
              API_RESULT.FAILED
            );
          }
          __DEV__ && console.log("Error 400: Bad Request", errorData);
          throw new Error(
            JSON.stringify({
              error: true,
              status: 400,
              data: errorData,
            })
          );
        } else if (status === 403) {
          updateParentByPostMessage(
            errorData?.responseMessage || "403",
            ErrorResponseCode.TIMEOUT.toString(),
            API_RESULT.FAILED
          );
          __DEV__ && console.log("Error 403: Forbidden");
          // print response from server
          let errorsRestriction = [
            ResponseMessages.OTP_INVALID,
            ResponseMessages.USER_BLOCKED,
          ];
          if (!errorsRestriction.includes(errorData?.responseMessage)) {
            handle403Error();
          }
          throw new Error(
            JSON.stringify({
              error: true,
              status: 403,
              data: errorData,
            })
          );
        } else if (status === 401) {
          updateParentByPostMessage(
            errorData?.responseMessage || "401",
            ErrorResponseCode.TIMEOUT.toString(),
            API_RESULT.FAILED
          );
          __DEV__ && console.log("Error 401: Unauthorized");
          handle401Error(error);
          throw new Error(
            JSON.stringify({
              error: true,
              status: 401,
              data: errorData,
            })
          );
        } else {
          __DEV__ && console.log(`Error ${status}:`, errorData);
          updateParentByPostMessage(
            errorData?.responseMessage || "500 or 504",
            ErrorResponseCode.GENERAL_ERROR.toString(),
            API_RESULT.FAILED
          );
          throw new Error(
            JSON.stringify({
              error: true,
              status,
              data: errorData,
            })
          );
        }
      } else {
        __DEV__ && console.log("No response from server:", error.message);
        updateParentByPostMessage(
          error.message || "500 or 504",
          ErrorResponseCode.GENERAL_ERROR.toString(),
          API_RESULT.FAILED
        );
        show403Popup(0, 3);
        throw new Error(
          JSON.stringify({
            error: true,
            status: 0,
            message: error.message || "No response from server",
          })
        );
      }
    } else {
      __DEV__ && console.log("Unexpected error:", error);
      updateParentByPostMessage(
        "500 or 504",
        ErrorResponseCode.GENERAL_ERROR.toString(),
        API_RESULT.FAILED
      );
      throw new Error(
        JSON.stringify({
          error: true,
          status: 0,
          message: "Unexpected error occurred",
        })
      );
    }
  }
};

const updateParentByPostMessage = (
  errorMessage: string,
  responseCode: string,
  status: API_RESULT
) => {
  sentPostMessage({
    errorMessage: errorMessage,
    responseCode: responseCode,
    status: status,
  });
};
const show403Popup = (attempts: number, maxAttempts: number) => {
  // check if digital, return
  const appStrings = store.getState().app.strings;
  if (appStrings?.t_error_popup_page_not_found_title) {
    showPopupWithMessage(
      appStrings.t_401_error_popup_title,
      appStrings.t_401_error_popup_body,
      appStrings,
      true,
      403,
      appStrings.t_401_error_popup_find_button
    );
  } else if (attempts < maxAttempts) {
    attempts++;
    setTimeout(show403Popup, 500); // Try again after 500ms
  } else {
    setTimeout(show403Popup, 500);
  }
};
const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const handle403Error = async () => {
  const currentSource = window.location.pathname
    .split("/")[1]
    .toLocaleLowerCase();
  const isFinalScreen = window.location.pathname.includes(
    ROUTES.SUMMARY_SCREEN
  );

  if (
    currentSource === URL_SOURCE_TYPE.DIGITAL ||
    currentSource === URL_SOURCE_TYPE.DIGITAL_AGENT ||
    isFinalScreen
  ) {
    return;
  }
  // Add a small delay to ensure strings are loaded
  await delay(500);

  show403Popup(0, 3);
  return;
};
const handle401Error = (e: any) => {
  __DEV__ && console.log("=== handle401Error ====", e);
  const appStrings = store.getState().app.strings;

  const currentSource = window.location.pathname
    .split("/")[1]
    .toLocaleLowerCase();

  const isFinalScreen = window.location.pathname.includes(
    ROUTES.SUMMARY_SCREEN
  );

  if (
    currentSource === URL_SOURCE_TYPE.DIGITAL ||
    currentSource === URL_SOURCE_TYPE.DIGITAL_AGENT ||
    isFinalScreen
  ) {
    return;
  }
  const relevantSources = [
    URL_SOURCE_TYPE.ENSURE,
    URL_SOURCE_TYPE.SMS,
    URL_SOURCE_TYPE.SMS_SHARE,
    URL_SOURCE_TYPE.APP,
    URL_SOURCE_TYPE.MARKETING,
    URL_SOURCE_TYPE.PFS_REP,
    URL_SOURCE_TYPE.CAMPAIGN,
    URL_SOURCE_TYPE.REP_MANUAL,
  ];

  if (relevantSources.includes(currentSource as URL_SOURCE_TYPE)) {
    showPopupWithMessage(
      appStrings.t_401_error_popup_title,
      appStrings.t_401_error_popup_body,
      appStrings,
      true,
      401
    );
    // logPfsAnalyticsEvent({
    //   timeout: true,
    // } as UpdateOnboardingAnalytics);
  }
};

export const updateAnaliticsWithKeepAlive = async (analyticsData: any) => {
  const url = `${BASE_URL}/cmd/update-analytics`;

  // Check if we have a valid authorization token before attempting the request
  const headers = getHeaders();

  if (!headers?.Authorization?.replace("Bearer ", "")?.trim()) {
    return;
  }

  try {
    if (!navigator.onLine) {
      return;
    }

    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(analyticsData),
      keepalive: true,
    });

    if (!response.ok) {
      if (response.status === 403) {
        show403Popup(0, 3);
      }
      throw new Error(`Request failed with status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) { }
};
